import {ParticularShift, Shift} from '@hconnect/common/types'
import {getRequiredShift} from '@hconnect/common/utils'
import {useUpmPlantNow} from '@hconnect/upm'
import {useQuery} from '@tanstack/react-query'
import {useCallback} from 'react'

type Params = {
  plantId: string
  shifts: Shift[]
}
/**
 * React Query hook to get the current shift for the plant.
 * Automatically caches and refetches based on when the current shift ends.
 *
 * @param plantId - The plant identifier
 * @param shifts - The shifts configuration for the plant
 * @returns The current ParticularShift with startDate and endDate
 */
export const useCurrentShift = ({plantId, shifts}: Params): ParticularShift => {
  const getPlantNow = useUpmPlantNow(plantId)

  const getCurrentShift = useCallback(() => {
    const nowMoment = getPlantNow()
    return getRequiredShift(nowMoment, shifts)
  }, [shifts, getPlantNow])

  const {data} = useQuery({
    queryKey: ['currentShift', plantId],
    queryFn: getCurrentShift,
    refetchInterval: () => {
      const currentShift = getCurrentShift()
      const timeUntilShiftEnds = currentShift.endDate.valueOf() - Date.now()
      return Math.max(0, timeUntilShiftEnds)
    },
    initialData: getCurrentShift
  })

  return data
}
